import { useMemo } from 'react';
import {
  Icon,
  Frame,
  SageClassnames,
  useSageBreakpoint,
} from '@kajabi/sage-react';

import { COACHING_PROGRAM_SESSIONS_ARRAY } from '../../../utils/constants';
import CalendarFilled from '../shared/CalendarFilled.png';

interface TransformedUnsplashObject {
  image_url: string;
  alt_description?: string;
}

interface PropsType {
  userData: any;
  previewImageUrls: TransformedUnsplashObject[];
  heroImageUrl: string;
}

const CoachingProgramPreview = ({
  userData,
  previewImageUrls,
  heroImageUrl,
}: PropsType) => {
  const coachingProgramData = useMemo(() => {
    return userData.ai_data.coaching_programs;
  }, [userData, previewImageUrls]);

  const breakpoints = useSageBreakpoint();

  const currentDate = new Date();
  const oneWeekAgo = new Date(currentDate.setDate(currentDate.getDate() - 7));
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(oneWeekAgo);

  return (
    <Frame
      style={{
        width: breakpoints.lg ? '100%' : '70%',
        border: breakpoints.sm ? '1rem solid #eceeef' : null,
        backgroundColor: breakpoints.sm ? '#eceeef' : null,
        borderRadius: breakpoints.sm ? '32px' : '15px',
        boxShadow: breakpoints.sm ? 'rgba(0, 0, 0, 0.1) 0px 20px 40px' : null,
      }}
      align={Frame.ALIGNMENTS.STRETCH_CENTER}
    >
      <Frame
        gap={Frame.GAPS.NONE}
        className={`container ${
          SageClassnames.SPACERS[breakpoints.md ? 'NONE' : 'LG_TOP']
        }`}
        style={{
          width: [breakpoints.md ? '100%' : '732px'],
          boxShadow: breakpoints.sm ? null : 'rgba(0, 0, 0, 0.1) 0px 20px 40px',
          borderRadius: breakpoints.sm ? '24px' : '15px',
          height: breakpoints.md ? '576px' : '720px',
          overflowY: 'scroll',
        }}
      >
        {/* Mock window component with 3 buttons */}
        {breakpoints.sm || (
          <Frame
            background="#ECEEEF"
            style={{
              height: '42px',
              width: breakpoints.md ? '652px' : '896px',
              borderTopLeftRadius: '15px',
              borderTopRightRadius: '15px',
            }}
          >
            <Frame
              direction={Frame.DIRECTIONS.HORIZONTAL}
              padding={Frame.PADDINGS.SM}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
              <div
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: '#B5BAC0',
                  borderRadius: '50%',
                  marginLeft: '-15px',
                }}
              ></div>
            </Frame>
          </Frame>
        )}

        {/* Coaching program navigation component */}
        <Frame
          background="#FFFFFF"
          style={{ height: '61px' }}
          direction={Frame.DIRECTIONS.HORIZONTAL}
          padding={Frame.PADDINGS.SM}
        >
          <Frame
            padding={Frame.PADDINGS.XS}
            direction={Frame.DIRECTIONS.HORIZONTAL}
          >
            <div
              style={{
                width: 87,
                height: breakpoints.sm ? 8 : 12,
                backgroundColor: '#D3D5D9',
                borderRadius: '100px',
              }}
            ></div>
          </Frame>
          {breakpoints.sm ? (
            <Frame
              padding={Frame.PADDINGS.NONE}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              gap={Frame.GAPS.XXS}
            >
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
              <div
                style={{
                  width: 32,
                  height: 4,
                  backgroundColor: '#D3D5D9',
                  borderRadius: '100px',
                }}
              />
            </Frame>
          ) : (
            <Frame
              padding={Frame.PADDINGS.XS}
              align={Frame.ALIGNMENTS.TOP_RIGHT}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
              <div
                style={{
                  width: 48,
                  height: 8,
                  backgroundColor: '#E9EBED',
                  borderRadius: '100px',
                }}
              ></div>
            </Frame>
          )}
        </Frame>

        {/* Coaching program hero image component */}
        <Frame
          style={{
            height: breakpoints.sm ? '196px' : '152px',
            backgroundImage: `
                            linear-gradient(rgba(0,0,0,.65), rgba(0,0,0,.65)),
                            url(${heroImageUrl}})
                        `,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
          padding={Frame.PADDINGS.LG}
          gap={Frame.GAPS.XS}
          align={Frame.ALIGNMENTS.CENTER_CENTER}
        >
          <Frame
            gap={Frame.GAPS.SM}
            style={{
              maxWidth: breakpoints.sm ? '100%' : '440px',
            }}
            direction={
              Frame.DIRECTIONS[breakpoints.sm ? 'VERTICAL' : 'HORIZONTAL']
            }
          >
            <Frame
              gap={Frame.GAPS.XS}
              style={{
                maxWidth: breakpoints.sm ? '100%' : '440px',
              }}
              direction={Frame.DIRECTIONS.VERTICAL}
            >
              <h5
                style={{
                  fontSize: breakpoints.md ? 24 : 20,
                  fontWeight: 700,
                }}
                className={`${SageClassnames.TYPE.HEADING_5} ${SageClassnames.TYPE_COLORS.WHITE}`}
              >
                {coachingProgramData.title}
              </h5>
              <div
                style={{
                  width: 105,
                  height: 8,
                  backgroundColor: '#DBE3EF',
                  borderRadius: '100px',
                }}
              ></div>
              <p
                style={{
                  fontSize: breakpoints.md ? 14 : 12,
                  fontWeight: 500,
                  lineHeight: '16px',
                }}
                className={`${SageClassnames.TYPE.BODY_SMALL_MED} ${SageClassnames.TYPE_COLORS.WHITE}`}
              >
                {coachingProgramData.description}
              </p>
            </Frame>
            <div
              style={{
                width: 83,
                height: 28,
                backgroundColor: '#0072EF',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <p style={{ fontSize: '12px', color: 'white', margin: 'auto' }}>
                Join now
              </p>
            </div>
          </Frame>
        </Frame>

        {/* Coaching program sessions component */}
        <Frame
          padding={Frame.PADDINGS[breakpoints.sm ? 'SM' : 'MD']}
          align={Frame.ALIGNMENTS.CENTER_CENTER}
          style={{ backgroundColor: 'white' }}
        >
          <Frame
            style={{
              width: '100%',
              maxWidth: '538px',
              border: '2px solid #ECEEEF',
              borderRadius: '10px',
            }}
            gap={Frame.GAPS.SM}
            padding={Frame.PADDINGS[breakpoints.sm ? 'SM' : 'MD']}
          >
            <Frame
              style={{
                height: '48px',
                border: '1px solid #ECEEEF',
                borderRadius: '10px',
                justifyContent: 'space-between',
              }}
              gap={Frame.GAPS.XS}
              padding={Frame.PADDINGS.SM}
              direction={Frame.DIRECTIONS.HORIZONTAL}
              className={SageClassnames.GRID_TEMPLATES.EM}
            >
              <div style={{ display: 'flex', minWidth: '53%' }}>
                <Icon
                  style={{ marginTop: '-3px' }}
                  color="sage-300"
                  icon="check-circle-filled"
                />
                <p
                  className={`${SageClassnames.TYPE.BODY_XSMALL_BOLD} ${SageClassnames.SPACERS.XS_LEFT} ${SageClassnames.TYPE_COLORS.CHARCOAL_200}`}
                  style={{ textDecoration: 'line-through' }}
                >
                  Session 1
                </p>
              </div>
              <p
                className={`${SageClassnames.TYPE.BODY_XSMALL} ${SageClassnames.TYPE_COLORS.CHARCOAL_100}`}
                style={{ textDecoration: 'line-through', textAlign: 'end' }}
              >
                {formattedDate} at 8:00am
              </p>
            </Frame>

            <Frame
              style={{
                height: breakpoints.sm ? null : '344px',
                border: '1px solid #ECEEEF',
                borderRadius: '10px',
                justifyContent: 'space-between',
              }}
              gap={Frame.GAPS.SM}
              padding={Frame.PADDINGS.SM}
              direction={Frame.DIRECTIONS.VERTICAL}
              className={SageClassnames.GRID_TEMPLATES.EM}
            >
              <Frame
                style={{
                  justifyContent: 'space-between',
                }}
                gap={Frame.GAPS.XS}
                direction={Frame.DIRECTIONS.HORIZONTAL}
                className={SageClassnames.GRID_TEMPLATES.EM}
              >
                <div style={{ display: 'flex', minWidth: '53%' }}>
                  <img
                    style={{ height: '16px', width: '16px' }}
                    src={CalendarFilled}
                  ></img>
                  <p
                    className={`${SageClassnames.TYPE.BODY_XSMALL_BOLD} ${SageClassnames.SPACERS.XS_LEFT} ${SageClassnames.TYPE_COLORS.CHARCOAL_400}`}
                  >
                    Session 2
                  </p>
                </div>
                <p
                  style={{ textAlign: 'end' }}
                  className={`${SageClassnames.TYPE.BODY_XSMALL} ${SageClassnames.TYPE_COLORS.CHARCOAL_300}`}
                >
                  Today at 8:00am
                </p>
              </Frame>
              <Frame
                className="preview-image"
                style={{
                  alignItems: 'center',
                  height: breakpoints.sm ? '100px' : '100%',
                  width: '100%',
                  backgroundImage: `
                                  linear-gradient(rgba(0,0,0,.65), rgba(0,0,0,.65)),
                                  url(${
                                    previewImageUrls.slice(-2)[0]?.image_url
                                  }})
                              `,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <Icon style={{ color: '#ECEEEF' }} icon="play" size="md" />
                </div>
              </Frame>
              <p className={SageClassnames.TYPE.BODY_XSMALL}>
                {coachingProgramData.session_description}
              </p>
              <Frame
                direction={Frame.DIRECTIONS.HORIZONTAL}
                style={{ justifyContent: 'space-between' }}
              >
                <p
                  className={`${SageClassnames.TYPE.BODY_XSMALL} ${SageClassnames.TYPE_COLORS.CHARCOAL_100}`}
                >
                  Reschedule
                </p>
                <div
                  style={{
                    width: 83,
                    height: 28,
                    backgroundColor: '#0072EF',
                    borderRadius: '6px',
                    display: 'flex',
                    alignSelf: 'center',
                  }}
                >
                  <p
                    style={{
                      fontSize: '12px',
                      color: 'white',
                      margin: 'auto',
                    }}
                  >
                    Join now
                  </p>
                </div>
              </Frame>
            </Frame>
            {COACHING_PROGRAM_SESSIONS_ARRAY.map(
              (sessionData: any, i: number) => (
                <Frame
                  key={`coaching-program-sessions-${i}`}
                  style={{
                    height: '48px',
                    border: '1px solid #ECEEEF',
                    borderRadius: '10px',
                    justifyContent: 'space-between',
                  }}
                  gap={Frame.GAPS.XS}
                  padding={Frame.PADDINGS.SM}
                  direction={Frame.DIRECTIONS.HORIZONTAL}
                  className={SageClassnames.GRID_TEMPLATES.EM}
                >
                  <div style={{ display: 'flex', minWidth: '53%' }}>
                    <img
                      style={{ height: '16px', width: '16px' }}
                      src={CalendarFilled}
                    ></img>
                    <p
                      className={`${SageClassnames.TYPE.BODY_XSMALL_BOLD} ${SageClassnames.SPACERS.XS_LEFT} ${SageClassnames.TYPE_COLORS.CHARCOAL_400}`}
                    >
                      {sessionData.sessionNumber}
                    </p>
                  </div>
                  <p
                    style={{ textAlign: 'end' }}
                    className={`${SageClassnames.TYPE.BODY_XSMALL} ${SageClassnames.TYPE_COLORS.CHARCOAL_300}`}
                  >
                    {sessionData.sessionDate}
                  </p>
                </Frame>
              )
            )}
          </Frame>
        </Frame>
      </Frame>
    </Frame>
  );
};

export default CoachingProgramPreview;
